import { tokensResponseSchema } from '@varga/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as authApi from '../api/auth';
import { API_URL } from './http';
import {
  getRefreshToken,
  getUsuario,
  limparSessao,
  setSessao,
  type UsuarioSessao,
} from './session';

interface AuthContexto {
  usuario: UsuarioSessao | null;
  /** ainda tentando restaurar a sessão a partir do refresh token guardado */
  carregando: boolean;
  entrar: (identificador: string, senha: string) => Promise<void>;
  registrarSessao: (r: {
    accessToken: string;
    refreshToken: string;
    usuario: UsuarioSessao;
  }) => void;
  sair: () => Promise<void>;
  /** chamado pelo cliente HTTP quando o refresh falha */
  encerrarLocal: () => void;
}

const Ctx = createContext<AuthContexto | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(getUsuario());
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    const restaurar = async (): Promise<void> => {
      const refresh = getRefreshToken();
      if (!refresh) {
        if (vivo) setCarregando(false);
        return;
      }
      try {
        const resp = await fetch(`${API_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        if (!resp.ok) throw new Error('refresh inválido');
        const dados = tokensResponseSchema.parse(await resp.json());
        const u = authApi.usuarioDeResposta(dados);
        if (!vivo) return;
        setSessao(dados.accessToken, dados.refreshToken, u);
        setUsuario(u);
      } catch {
        limparSessao();
      } finally {
        if (vivo) setCarregando(false);
      }
    };
    void restaurar();
    return () => {
      vivo = false;
    };
  }, []);

  const entrar = useCallback(async (identificador: string, senha: string): Promise<void> => {
    const dados = await authApi.login(identificador, senha);
    const u = authApi.usuarioDeResposta(dados);
    setSessao(dados.accessToken, dados.refreshToken, u);
    setUsuario(u);
  }, []);

  const registrarSessao = useCallback(
    (r: { accessToken: string; refreshToken: string; usuario: UsuarioSessao }): void => {
      setSessao(r.accessToken, r.refreshToken, r.usuario);
      setUsuario(r.usuario);
    },
    [],
  );

  const sair = useCallback(async (): Promise<void> => {
    await authApi.logout();
    limparSessao();
    setUsuario(null);
  }, []);

  const encerrarLocal = useCallback((): void => {
    limparSessao();
    setUsuario(null);
  }, []);

  const valor = useMemo<AuthContexto>(
    () => ({ usuario, carregando, entrar, registrarSessao, sair, encerrarLocal }),
    [usuario, carregando, entrar, registrarSessao, sair, encerrarLocal],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContexto {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth fora de <AuthProvider>');
  return ctx;
}
